/**
 * Global error handler with security considerations
 * Prevents sensitive information leakage while maintaining good UX
 */

import { logger } from '@/utils/logger';
import { toast } from 'sonner';

// Define error types for better handling
export enum ErrorType {
  VALIDATION = 'VALIDATION_ERROR',
  AUTHENTICATION = 'AUTH_ERROR',
  AUTHORIZATION = 'AUTHZ_ERROR',
  NETWORK = 'NETWORK_ERROR',
  SERVER = 'SERVER_ERROR',
  UNKNOWN = 'UNKNOWN_ERROR'
}

// Safe error messages that don't leak sensitive information
const SAFE_ERROR_MESSAGES = {
  [ErrorType.VALIDATION]: 'Please check your input and try again.',
  [ErrorType.AUTHENTICATION]: 'Authentication failed. Please check your credentials.',
  [ErrorType.AUTHORIZATION]: 'You do not have permission to perform this action.',
  [ErrorType.NETWORK]: 'Network error. Please check your connection and try again.',
  [ErrorType.SERVER]: 'Server error occurred. Please try again later.',
  [ErrorType.UNKNOWN]: 'An unexpected error occurred. Please try again.'
};

// Sensitive patterns that should not be exposed to users
const SENSITIVE_PATTERNS = [
  /password/i,
  /token/i,
  /secret/i,
  /key/i,
  /credential/i,
  /auth/i,
  /session/i,
  /cookie/i,
  /database/i,
  /sql/i,
  /query/i,
  /connection/i,
  /server/i,
  /internal/i,
  /stack trace/i,
  /line \d+/i,
  /at .+\.js:\d+/i
];

/**
 * Check if error message contains sensitive information
 */
const containsSensitiveInfo = (message: string): boolean => {
  return SENSITIVE_PATTERNS.some(pattern => pattern.test(message));
};

/**
 * Sanitize error message for user display
 */
const sanitizeErrorMessage = (message: string): string => {
  if (!message || typeof message !== 'string') {
    return SAFE_ERROR_MESSAGES[ErrorType.UNKNOWN];
  }

  // If message contains sensitive info, return generic message
  if (containsSensitiveInfo(message)) {
    return SAFE_ERROR_MESSAGES[ErrorType.SERVER];
  }

  // Remove potential stack traces and file paths
  const cleanMessage = message
    .replace(/\s+at\s+.+\(.+\)/g, '') // Remove stack trace lines
    .replace(/\s+at\s+.+:\d+:\d+/g, '') // Remove file:line:column references
    .replace(/Error:\s*/i, '') // Remove "Error:" prefix
    .trim();

  // If cleaned message is too short or empty, return generic message
  if (cleanMessage.length < 5) {
    return SAFE_ERROR_MESSAGES[ErrorType.UNKNOWN];
  }

  return cleanMessage;
};

/**
 * Determine error type from error object
 */
const determineErrorType = (error: any): ErrorType => {
  if (!error) return ErrorType.UNKNOWN;

  const message = error.message || error.toString() || '';
  const code = error.code || error.status || 0;

  // Network errors
  if (message.includes('NetworkError') || message.includes('fetch') || code === 0) {
    return ErrorType.NETWORK;
  }

  // Authentication errors
  if (code === 401 || message.includes('unauthorized') || message.includes('authentication')) {
    return ErrorType.AUTHENTICATION;
  }

  // Authorization errors
  if (code === 403 || message.includes('forbidden') || message.includes('permission')) {
    return ErrorType.AUTHORIZATION;
  }

  // Validation errors
  if (code === 400 || message.includes('validation') || message.includes('invalid')) {
    return ErrorType.VALIDATION;
  }

  // Server errors
  if (code >= 500 || message.includes('server') || message.includes('internal')) {
    return ErrorType.SERVER;
  }

  return ErrorType.UNKNOWN;
};

/**
 * Log error securely (for development and monitoring)
 */
const logError = (error: any, context?: string): void => {
  const timestamp = new Date().toISOString();
  const errorType = determineErrorType(error);
  
  // Create sanitized log entry
  const logEntry = {
    timestamp,
    type: errorType,
    context: context || 'Unknown',
    message: error.message || 'No message',
    stack: process.env.NODE_ENV === 'development' ? error.stack : undefined,
    url: typeof window !== 'undefined' ? window.location.href : undefined,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined
  };

  // Log to console in development, would send to monitoring service in production
  if (process.env.NODE_ENV === 'development') {
    logger.error('🔒 Secure Error Log:', logEntry);
  }

  // TODO: In production, send to monitoring service (e.g., Sentry, LogRocket)
  // sendToMonitoringService(logEntry);
};

/**
 * Handle error with user notification and secure logging
 */
export const handleError = (error: any, context?: string, showToast: boolean = true): void => {
  // Log error securely
  logError(error, context);

  // Determine error type and get safe message
  const errorType = determineErrorType(error);
  const safeMessage = sanitizeErrorMessage(error?.message || error?.toString());

  // Show user-friendly toast notification
  if (showToast) {
    switch (errorType) {
      case ErrorType.VALIDATION:
        toast.error(safeMessage);
        break;
      case ErrorType.AUTHENTICATION:
        toast.error('Authentication required. Please log in again.');
        break;
      case ErrorType.AUTHORIZATION:
        toast.error('Access denied. Contact your administrator if this persists.');
        break;
      case ErrorType.NETWORK:
        toast.error('Connection error. Please check your internet connection.');
        break;
      default:
        toast.error('Something went wrong. Please try again.');
    }
  }
};

/**
 * Handle async operations with error handling
 */
export const withErrorHandler = async (
  operation: () => Promise<any>,
  context?: string,
  showToast: boolean = true
) => {
  try {
    return await operation();
  } catch (error) {
    handleError(error, context, showToast);
    return null;
  }
};

/**
 * React error boundary handler
 */
export const handleReactError = (error: Error, errorInfo: any): void => {
  logError(error, 'React Error Boundary');
  
  // Don't show toast for React errors as they're usually component-level
  logger.error('React Error:', error, errorInfo);
};

/**
 * Global unhandled error handler
 */
export const setupGlobalErrorHandlers = (): void => {
  // Only set up in browser environment
  if (typeof window === 'undefined') {
    return;
  }

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', (event) => {
    handleError(event.reason, 'Unhandled Promise Rejection', false);
    event.preventDefault(); // Prevent default browser behavior
  });

  // Handle uncaught errors
  window.addEventListener('error', (event) => {
    handleError(event.error, 'Uncaught Error', false);
  });

  recoverFromStaleChunks();
};

/** How recently a reload has to have happened to count as a loop, in ms. */
const RELOAD_LOOP_WINDOW_MS = 15_000;
const LAST_RELOAD_KEY = 'waq:stale-chunk-reload-at';

/**
 * Reload once when a lazy chunk has been deployed out from under the tab.
 *
 * `deploy:prod` rsyncs with `--delete`, so the hashed chunks an already-open
 * tab still points at stop existing the moment it finishes. The next lazy route
 * the visitor opens then throws "error loading dynamically imported module" and
 * the button appears to do nothing -- which is how an admin came to report that
 * a fix had not shipped when it had. Vite fires `vite:preloadError` for exactly
 * this case, and index.html is served `no-store`, so a reload always lands on
 * the current build.
 *
 * Guarded on a timestamp: a chunk that is genuinely missing would otherwise
 * reload forever. A second deploy later in the same session is far outside the
 * window and still recovers.
 */
function recoverFromStaleChunks(): void {
  window.addEventListener('vite:preloadError', (event) => {
    const lastReload = Number(sessionStorage.getItem(LAST_RELOAD_KEY) ?? 0);
    if (Date.now() - lastReload < RELOAD_LOOP_WINDOW_MS) {
      // Reloading did not help, so let the error surface rather than loop.
      return;
    }

    event.preventDefault();
    sessionStorage.setItem(LAST_RELOAD_KEY, String(Date.now()));
    window.location.reload();
  });
}

// Custom error classes for specific scenarios
export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

export class AuthenticationError extends Error {
  constructor(message: string = 'Authentication failed') {
    super(message);
    this.name = 'AuthenticationError';
  }
}

export class AuthorizationError extends Error {
  constructor(message: string = 'Access denied') {
    super(message);
    this.name = 'AuthorizationError';
  }
}

export class NetworkError extends Error {
  constructor(message: string = 'Network error occurred') {
    super(message);
    this.name = 'NetworkError';
  }
}

export default {
  handleError,
  withErrorHandler,
  handleReactError,
  setupGlobalErrorHandlers,
  ValidationError,
  AuthenticationError,
  AuthorizationError,
  NetworkError,
  ErrorType
};